import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  CalendarCheck, Store, LogIn, LogOut, Plus, Pencil,
  AlertTriangle, Clock, TrendingUp, CalendarDays, ScanFace, ShieldCheck,
} from "lucide-react";
import FaceEnrollments from "../face/FaceEnrollments";
import FaceVerifications from "../face/FaceVerifications";
import { listAttendance, getAttendanceSummary } from "../../api/attendance";
import { listBranches } from "../../api/branches";
import { listEmployees } from "../../api/employees";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import {
  Card, Button, Input, Select, EmptyState, TableSkeleton, StatCard, Skeleton, Avatar,
} from "../../components/ui";
import Pagination from "../../components/Pagination";
import AttendanceEntryModal from "./AttendanceEntryModal";
import Coordinates from "../../components/Coordinates";
import { DateRangePresets, ActiveFilters } from "../../components/Filters";
import {
  unwrapList, refLabel, formatDate, formatTime, formatMinutes, formatDateTime, errorMessage, monthRange,
} from "../../lib/records";
import { ATTENDANCE_STATUSES, ATTENDANCE_STATUS_LABELS, ATTENDANCE_STATUS_STYLES } from "../../lib/enums";

const PAGE_SIZE = 10;

function AttendanceChip({ status }) {
  if (!status) return <span className="text-gray-400">—</span>;
  const style = ATTENDANCE_STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-label-sm font-semibold ${style}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {ATTENDANCE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function Punch({ punch, icon: Icon, tone }) {
  if (!punch?.at) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex items-start gap-1.5">
      <Icon size={13} className={`mt-0.5 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-gray-700">
          {formatTime(punch.at)}
          {punch.source && punch.source !== "EMPLOYEE" && (
            <span
              className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700"
              title="Time set from the panel — not the device's own punch"
            >
              {punch.source === "PARTNER" ? "edited" : punch.source.toLowerCase()}
            </span>
          )}
        </p>
        <Coordinates location={punch.location} size="sm" />
      </div>
    </div>
  );
}

export default function AttendanceList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const bounds = monthRange();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE, pages: 1 });
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState(bounds.from);
  const [to, setTo] = useState(bounds.to);
  const [status, setStatus] = useState("");
  const [employeeId, setEmployeeId] = useState(() => searchParams.get("employee") || "");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [tab, setTab] = useState("punches"); // "punches" | "enrollment" | "verifications"

  const [entryOpen, setEntryOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  useEffect(() => {
    listEmployees({ limit: 100, status: "ACTIVE", ...(isChain && branchId ? { partner: branchId } : {}) })
      .then((res) => setEmployees(unwrapList(res, 100).items))
      .catch(() => setEmployees([]));
  }, [branchId, isChain]);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    getAttendanceSummary({
      from,
      to,
      ...(employeeId ? { employee: employeeId } : {}),
      ...(isChain && branchId ? { partner: branchId } : {}),
    })
      .then((res) => !cancelled && setSummary(res?.data ?? null))
      .catch(() => !cancelled && setSummary(null))
      .finally(() => !cancelled && setSummaryLoading(false));
    return () => {
      cancelled = true;
    };
  }, [from, to, employeeId, branchId, isChain]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listAttendance({
        page,
        limit: PAGE_SIZE,
        from,
        to,
        ...(status ? { status } : {}),
        ...(employeeId ? { employee: employeeId } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      const { items: list, pagination: meta } = unwrapList(res, PAGE_SIZE);
      setItems(list);
      setPagination(meta);
    } catch (err) {
      setItems([]);
      setLoadError(errorMessage(err, "Attendance could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [page, from, to, status, employeeId, branchId, isChain]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [from, to, status, employeeId, branchId]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (employeeId) next.set("employee", employeeId);
    else next.delete("employee");
    setSearchParams(next, { replace: true });
  }, [employeeId]);

  function afterSave() {
    setEntryOpen(false);
    setEditingRecord(null);
    fetchData();
  }

  const totals = summary?.totals;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Attendance"
          subtitle="Punches come from the employee app with a GPS fix. Correct anything that's wrong here."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {isChain && (
                <div className="flex items-center gap-2">
                  <Store size={15} className="shrink-0 text-gray-400" />
                  <Select
                    value={branchId}
                    onChange={(e) => {
                      setBranchId(e.target.value);
                      setEmployeeId("");
                    }}
                    className="w-full sm:w-48 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <Button
                onClick={() => {
                  setEditingRecord(null);
                  setEntryOpen(true);
                }}
                className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 text-sm px-3 sm:px-4 py-1.5 sm:py-2"
              >
                <Plus size={16} className="hidden sm:inline mr-1" /> Record a day
              </Button>
            </div>
          }
        />

        {/* Tab bar */}
        <div className="flex items-center gap-1 mb-4 rounded-xl bg-white border border-gray-100 shadow-sm p-1 w-fit">
          {[
            { key: "punches",       label: "Punches",      icon: CalendarCheck },
            { key: "enrollment",    label: "Face Enrol",   icon: ScanFace      },
            { key: "verifications", label: "Verifications",icon: ShieldCheck   },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${
                tab === t.key
                  ? "bg-emerald-500 text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>

        {tab === "enrollment" && <FaceEnrollments onSwitchView={setTab} />}
        {tab === "verifications" && <FaceVerifications onSwitchView={setTab} />}
        {tab === "punches" && <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {summaryLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-12 sm:w-16" />
              </Card>
            ))
          ) : (
            <>
              <StatCard label="Payable days" value={totals?.payableDays ?? 0} icon={CalendarDays} tone="primary" />
              <StatCard label="Present" value={totals?.presentDays ?? 0} icon={CalendarCheck} tone="green" />
              <StatCard label="Hours worked" value={totals?.workedHours ?? 0} icon={Clock} tone="blue" />
              <StatCard
                label="Avg hrs/day"
                value={totals?.averageHoursPerWorkedDay ?? 0}
                icon={TrendingUp}
                tone="purple"
              />
            </>
          )}
        </div>


        <Card className="mt-4 sm:mt-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <DateRangePresets from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />

            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  title="From date"
                  className="w-36 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                />
                <span className="text-sm text-gray-400">to</span>
                <Input
                  type="date"
                  value={to}
                  min={from}
                  onChange={(e) => setTo(e.target.value)}
                  title="To date"
                  className="w-36 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                />
              </div>
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                className="flex-1 min-w-[10rem] border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="">Everyone</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </Select>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="sm:w-40 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="">All statuses</option>
                {ATTENDANCE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {ATTENDANCE_STATUS_LABELS[s]}
                  </option>
                ))}
              </Select>
            </div>

            <ActiveFilters
              resultCount={loading ? null : pagination.total}
              filters={[
                {
                  label: "Employee",
                  value: employees.find((e) => e._id === employeeId)?.name,
                  onClear: () => setEmployeeId(""),
                },
                {
                  label: "Status",
                  value: status ? ATTENDANCE_STATUS_LABELS[status] : "",
                  onClear: () => setStatus(""),
                },
                {
                  label: "Branch",
                  value: branches.find((b) => b._id === branchId)?.name,
                  onClear: () => setBranchId(""),
                },
              ]}
              onClearAll={() => {
                setEmployeeId("");
                setStatus("");
                setBranchId("");
              }}
            />
          </div>

          {loadError && !loading && (
            <div className="border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{loadError}</div>
          )}

          {loading ? (
            <TableSkeleton rows={6} cols={6} />
          ) : items.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={CalendarCheck}
                title="No attendance in this range"
                description="Widen the dates, or record a day manually for approved leave and missed punches."
                action={
                  <Button
                    onClick={() => {
                      setEditingRecord(null);
                      setEntryOpen(true);
                    }}
                    className="bg-gradient-to-r from-emerald-500 to-green-600 text-white"
                  >
                    <Plus size={16} /> Record a day
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Employee</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Date</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Status</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">In</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Out</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Worked</th>
                    {isChain && (
                      <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden lg:table-cell">Branch</th>
                    )}
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-right w-16">Edit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {items.map((rec) => (
                    <Fragment key={rec._id}>
                    <tr
                      onClick={() => setExpandedId(expandedId === rec._id ? null : rec._id)}
                      className="cursor-pointer text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200"
                    >
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={refLabel(rec.employee, "?")} size={26} />
                          <div className="min-w-0">
                            <p className="truncate text-xs font-medium text-gray-800">
                              {refLabel(rec.employee, "Unknown")}
                            </p>
                            {rec.employee?.designation && (
                              <p className="truncate text-xs text-gray-500">{rec.employee.designation}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-gray-600">{formatDate(rec.date)}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <AttendanceChip status={rec.status} />
                        {rec.isOpen && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            <Clock size={9} /> Pending
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <Punch punch={rec.checkIn} icon={LogIn} tone="text-emerald-500" />
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <Punch punch={rec.checkOut} icon={LogOut} tone="text-gray-400" />
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <p className="font-medium text-gray-800">{formatMinutes(rec.workedMinutes)}</p>
                        {rec.payableDay != null && (
                          <p className="text-xs text-gray-500">{rec.payableDay} day payable</p>
                        )}
                      </td>
                      {isChain && (
                        <td className="px-3 sm:px-4 py-2 sm:py-3 hidden lg:table-cell text-gray-600">
                          {refLabel(rec.partner, "Head office")}
                        </td>
                      )}
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingRecord(rec);
                            setEntryOpen(true);
                          }}
                          title="Correct this day"
                          className="rounded-md p-1.5 text-gray-400 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                        >
                          <Pencil size={15} />
                        </button>
                      </td>
                    </tr>

                    {expandedId === rec._id && (
                      <tr className="bg-emerald-50/30">
                        <td colSpan={isChain ? 8 : 7} className="px-3 sm:px-4 py-3">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Against their shift
                              </p>
                              {rec.expectedMinutes ? (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                  <dt className="text-gray-500">Expected</dt>
                                  <dd className="font-medium text-gray-800">{formatMinutes(rec.expectedMinutes)}</dd>
                                  <dt className="text-gray-500">Worked</dt>
                                  <dd className="font-medium text-gray-800">{formatMinutes(rec.workedMinutes)}</dd>
                                  {rec.lateByMinutes > 0 && (
                                    <>
                                      <dt className="text-gray-500">Late by</dt>
                                      <dd className="font-medium text-amber-700">{formatMinutes(rec.lateByMinutes)}</dd>
                                    </>
                                  )}
                                  {rec.shortfallMinutes > 0 && (
                                    <>
                                      <dt className="text-gray-500">Short by</dt>
                                      <dd className="font-medium text-red-600">{formatMinutes(rec.shortfallMinutes)}</dd>
                                    </>
                                  )}
                                  {rec.overtimeMinutes > 0 && (
                                    <>
                                      <dt className="text-gray-500">Overtime</dt>
                                      <dd className="font-medium text-emerald-700">{formatMinutes(rec.overtimeMinutes)}</dd>
                                    </>
                                  )}
                                  <dt className="text-gray-500">Working day</dt>
                                  <dd className="font-medium text-gray-800">
                                    {rec.isWorkingDay === true ? "Yes" : rec.isWorkingDay === false ? "No" : "—"}
                                  </dd>
                                </dl>
                              ) : (
                                <p className="text-xs text-gray-500">
                                  No shift set for this employee, so the day wasn't measured against one — only
                                  recorded days count toward pay.{" "}
                                  <Link
                                    to={`/employees/${rec.employee?._id}`}
                                    className="font-semibold text-emerald-700 underline"
                                  >
                                    Set a shift
                                  </Link>
                                </p>
                              )}
                              {rec.note && (
                                <p className="mt-2 text-xs italic text-gray-600">“{rec.note}”</p>
                              )}
                            </div>

                            <div>
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Audit trail
                              </p>
                              <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                                {(rec.logs ?? []).map((log, li) => (
                                  <li key={li} className="rounded bg-white/70 px-2.5 py-1.5">
                                    <span className="font-medium text-gray-800">
                                      {log.action?.replace(/_/g, " ").toLowerCase()}
                                    </span>
                                    <span className="text-gray-400"> · {formatDateTime(log.at)}</span>
                                    {log.byType && <span className="text-gray-400"> · {log.byType}</span>}
                                    {log.changes &&
                                      Object.entries(log.changes).map(([field, pair]) => {
                                        const [before, after] = Array.isArray(pair) ? pair : [null, pair];
                                        const fmt = (v) =>
                                          v == null
                                            ? "—"
                                            : /^\d{4}-\d{2}-\d{2}T/.test(String(v))
                                              ? formatDateTime(v)
                                              : String(v);
                                        return (
                                          <span key={field} className="block text-gray-500">
                                            {field}: {fmt(before)} → <strong className="text-gray-700">{fmt(after)}</strong>
                                          </span>
                                        );
                                      })}
                                    {log.note && <span className="block italic text-gray-500">“{log.note}”</span>}
                                  </li>
                                ))}
                                {(rec.logs ?? []).length === 0 && (
                                  <li className="text-gray-500">No activity recorded.</li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="px-3 sm:px-4 border-t border-emerald-100/60 pt-3 sm:pt-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-gray-500">
          Payable days count present, leave, holiday and week-off as 1, half days as 0.5 and absences
          as 0. Off-site punches are flagged against your saved branch location, never blocked.
        </p>

        <AttendanceEntryModal
          open={entryOpen}
          onClose={() => {
            setEntryOpen(false);
            setEditingRecord(null);
          }}
          record={editingRecord}
          employees={employees}
          onSaved={afterSave}
        />
        </>}
      </div>
    </div>
  );
}
